import { createSelector } from 'reselect';

import { __ } from '../../../../i18n/l10n';
import { getMatchIdFromProps, getSportIdFromProps, getTeamIdFromProps } from '../../../../selectors/_propsGetters_';
import {
  selectEntitiesMatchesEntityFieldImg,
  selectEntitiesMatchesEntityOfficialLineups,
  selectEntitiesMatchesEntityStatus,
} from '../../../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import {
  selectIsMatchMMLoading,
  makeMatchTeamCompetitorPlayersArraySelector,
  selectMatchTeamFormation,
} from '../../../../selectors/match.selector';
import { selectMyMatchModeMatchIds } from '../../../../selectors/mymatch/mymatchPrimary.selector';
import { MatchStatuses } from '../../../../types/match';
import { type MatchViewMode } from '../../../../types/matchView';
import { MatchViewModes } from '../../../../types/matchView';
import { type SelectorMaker, type Selector } from '../../../../types/selectors';
import { SportIds } from '../../../../types/sports';
import {
  LineUpFixedFormationMappingBySportId,
  LineUpIceHockeyFormationMappingByLine,
  SportsWithFixedFormation,
  SportsWithNoTab,
} from '../../constants/runtime';

const emptyArray: [] = [];

type LineUpFieldPropsType = {
  formedPlayerIds: Array<Array<number>>;
  goalKeeperPlayerId?: number;
  isMMLoading: boolean;
  fieldImg?: string;
  infoLabel?: string;
};

const makeLineUpFieldPropsSelector: SelectorMaker<LineUpFieldPropsType> = () => {
  const selectFieldPlayerIds: Selector<Array<number>> = createSelector(
    [makeMatchTeamCompetitorPlayersArraySelector(), getSportIdFromProps, getTeamIdFromProps],
    (allPlayers, sportId, teamId) => {
      const fieldPlayerIds: Array<number> = [];
      const hasFixedFormation = sportId ? SportsWithFixedFormation.indexOf(sportId) !== -1 : emptyArray;
      const isIceHockey = sportId === SportIds.ICEHOCKEY;

      for (let i = 0; i < allPlayers.length; i++) {
        const player = allPlayers[i];
        const { starter, lineupOrder, playerId, team } = player;

        if (typeof lineupOrder === 'number' && (starter || isIceHockey)) {
          if (sportId === SportIds.ICEHOCKEY) {
            fieldPlayerIds[lineupOrder - 1 + (team === 2 ? 18 : 0)] = playerId;
          } else if (sportId && SportsWithNoTab.indexOf(sportId) !== -1) {
            fieldPlayerIds[lineupOrder - 1 + (team === 2 ? 5 : 0)] = playerId;
          } else {
            fieldPlayerIds[lineupOrder - 1] = playerId;
          }
        }
      }

      if (sportId && hasFixedFormation && fieldPlayerIds.length > 0) {
        if (isIceHockey && teamId) {
          return LineUpIceHockeyFormationMappingByLine[teamId]?.map((index) => fieldPlayerIds[index]) ?? emptyArray;
        }
        return LineUpFixedFormationMappingBySportId[sportId]?.map((index) => fieldPlayerIds[index]) ?? emptyArray;
      }

      return fieldPlayerIds;
    },
  );

  const selectFormedPlayerIds: Selector<Array<Array<number>>> = createSelector(
    [selectFieldPlayerIds, selectMatchTeamFormation, getSportIdFromProps],
    (fieldPlayerIds, teamFormation, sportId) => {
      const formationArr = teamFormation?.split('-').map((k) => parseInt(k, 10));
      let [, ...playerIds] = fieldPlayerIds;
      if (sportId && SportsWithFixedFormation.indexOf(sportId) !== -1) {
        playerIds = fieldPlayerIds;
      }

      const result = [];

      let absoluteIndex = 0;
      for (let j = 0; j < formationArr.length; j++) {
        const nbColumns = formationArr[j];

        result.push(playerIds.slice(absoluteIndex, absoluteIndex + nbColumns).reverse());

        absoluteIndex += nbColumns;
      }

      return result.reverse();
    },
  );

  const selectGoalKeeperPlayerId: Selector<number | undefined> = createSelector(
    [selectFieldPlayerIds, getSportIdFromProps],
    (fieldPlayerIds, sportId) => {
      if (sportId && SportsWithFixedFormation.indexOf(sportId) !== -1) {
        return undefined;
      }
      return fieldPlayerIds[0];
    },
  );

  const selectMatchViewMode: Selector<MatchViewMode> = createSelector(
    [getMatchIdFromProps, selectMyMatchModeMatchIds],
    (matchId, myMatchModeMatchIds) =>
      matchId && myMatchModeMatchIds.indexOf(matchId) !== -1 ? MatchViewModes.MY_MATCH : MatchViewModes.CLASSIC,
  );

  const selectInfoLabel: Selector<string | undefined> = createSelector(
    [selectEntitiesMatchesEntityStatus, getSportIdFromProps, selectEntitiesMatchesEntityOfficialLineups],
    (matchStatus, sportId, officialLineups) => {
      const isPrematch = matchStatus?.toLowerCase() === MatchStatuses.PREMATCH;

      if (isPrematch && sportId === SportIds.BASKETBALL) {
        return officialLineups ? __('Official starting lineups') : __('Probable starting lineups');
      } else if (isPrematch && sportId === SportIds.ICEHOCKEY) {
        return officialLineups ? __('Official lineups') : __('Projected lineups');
      }
    },
  );

  return createSelector(
    [
      selectFormedPlayerIds,
      selectGoalKeeperPlayerId,
      selectMatchViewMode,
      selectIsMatchMMLoading,
      selectEntitiesMatchesEntityFieldImg,
      selectInfoLabel,
    ],
    (formedPlayerIds, goalKeeperPlayerId, matchViewMode, isMMLoading, fieldImg, infoLabel) => ({
      formedPlayerIds,
      goalKeeperPlayerId,
      isMMLoading,
      fieldImg,
      infoLabel,
    }),
  );
};

export default makeLineUpFieldPropsSelector;
